import { CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";
import type { AdvancementRow } from "../../metrics/computeChallengeAdvancement";
import type { ReportMetrics } from "../../metrics/types";
import { formatCurrency } from "./formatValue";

function AccountProgress({ row, currency }: { row: AdvancementRow; currency: string }) {
  const t = useTranslation();
  // La barre est bornée à 100 % : un ratio > 1 ne doit pas déborder de la piste.
  const widthPct = Math.min(Math.max(row.ratio, 0), 1) * 100;

  const subtitle = [row.propFirmName, row.tierLabel].filter(Boolean).join(" · ");

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{row.name}</div>
          {subtitle && (
            <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-mono text-foreground">
            {formatCurrency(row.profit, currency)}
            {row.targetAmount !== null && (
              <span className="text-muted-foreground">
                {" / "}
                {formatCurrency(row.targetAmount, currency)}
              </span>
            )}
          </div>
          {row.targetAmount !== null && (
            <div className="text-xs text-muted-foreground">{t.reports.advancementTarget}</div>
          )}
        </div>
      </div>

      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full ${row.isComplete ? "bg-emerald-500" : "bg-primary"}`}
          style={{ width: `${widthPct}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          {t.reports.advancementDays}
          {": "}
          <span className="font-mono">
            {row.daysTraded}
            {row.minTradingDays !== null && ` / ${row.minTradingDays}`}
          </span>
        </div>
        {row.isComplete && (
          <div className="flex items-center gap-1 text-xs text-emerald-500">
            <CheckCircle2 className="h-3 w-3" />
            {t.reports.advancementComplete}
          </div>
        )}
      </div>
    </div>
  );
}

/** Avancement des challenges encore en cours à la fin de la période.
 *  Retourne null si le snapshot est en v1 (challengeAdvancement absent/null). */
export function AdvancementSection({ metrics }: { metrics: ReportMetrics }) {
  const t = useTranslation();
  const advancement = metrics.challengeAdvancement;
  if (!advancement) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t.reports.advancementTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        {advancement.accounts.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t.reports.advancementNone}</div>
        ) : (
          <div className="space-y-5">
            {advancement.accounts.map((row, i) => (
              <AccountProgress key={`${row.name}-${i}`} row={row} currency={metrics.currency} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
